import { useMemo } from "react"
import { FilterOperator, LogicalOperator, Status, useGetPermissionsSuspenseQuery } from "src/graphql/quark/quark.type"
import { capitalizeFirstLetter } from "src/shared/utils/data-helpers"
import { groupBy } from "src/shared/utils/groupBy"

export type PermissionAction = {
  id: string
  label: string
}

export type PermissionSubModule = {
  id: string
  label: string
  actions: PermissionAction[]
}

export type PermissionModule = {
  id: string
  label: string
  subModules: PermissionSubModule[]
  actionIds: string[]
}

const formatLabel = (value?: string | null) =>
  capitalizeFirstLetter((value ?? "").replace(/[-_]/g, " ").replace(/\s+/g, " ").trim())

export const usePermissionsTreeViewItems = () => {
  const { data: respPermissions } = useGetPermissionsSuspenseQuery({
    errorPolicy: "ignore",
    variables: {
      filter: [
        {
          logicalOperator: LogicalOperator.And,
          conditions: [
            {
              operator: FilterOperator.Like,
              value: Status.Active,
              field: "status"
            }
          ]
        }
      ],
      page: 1,
      limit: 100000
    }
  })

  const permissions = respPermissions?.getPermissions.data ?? []

  const modules = useMemo((): PermissionModule[] => {
    const groupedByModule = groupBy(permissions, (val) => val.module)

    return Object.entries(groupedByModule)
      .map(([moduleName, modulePermissions]) => {
        const subModules = Object.entries(groupBy(modulePermissions, (val) => val.subModule))
          .map(([subModuleName, subModulePermissions]) => {
            const actions = subModulePermissions
              .map((permission) => ({
                id: permission.id as string,
                label: formatLabel(permission.action)
              }))
              .sort((a, b) => a.label.localeCompare(b.label))

            return {
              id: `${moduleName}~${subModuleName}`,
              label: formatLabel(subModuleName),
              actions
            }
          })
          .sort((a, b) => a.label.localeCompare(b.label))

        return {
          id: moduleName,
          label: formatLabel(moduleName),
          subModules,
          actionIds: subModules.flatMap((sub) => sub.actions.map((action) => action.id))
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [permissions])

  const allActionIds = useMemo(() => modules.flatMap((module) => module.actionIds), [modules])

  return { modules, allActionIds }
}
